import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import BeachAccessOutlinedIcon from '@mui/icons-material/BeachAccessOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import DevicesOtherOutlinedIcon from '@mui/icons-material/DevicesOtherOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import PolicyOutlinedIcon from '@mui/icons-material/PolicyOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { P } from '@hrms/shared';
import { ReactNode } from 'react';

export interface NavItem {
  label: string;
  to: string;
  icon: ReactNode;
  /** Visible when the user holds any of these (UX only — the API enforces). */
  anyOf?: string[];
  /** Require a grant beyond SELF (admin/manager views). */
  others?: boolean;
  children?: Omit<NavItem, 'icon' | 'children'>[];
}

export const NAV_SECTIONS: { title: string; items: NavItem[] }[] = [
  {
    title: 'My workspace',
    items: [
      { label: 'Dashboard', to: '/app/dashboard', icon: <DashboardOutlinedIcon /> },
      {
        label: 'My self-service',
        to: '/self',
        icon: <PersonOutlineIcon />,
        children: [
          { label: 'Overview', to: '/self' },
          { label: 'Profile', to: '/self/profile' },
          { label: 'Onboarding', to: '/self/onboarding' },
          { label: 'Leave', to: '/self/leave' },
          { label: 'Assets', to: '/self/assets' },
          { label: 'Documents', to: '/self/documents' },
        ],
      },
    ],
  },
  {
    title: 'People',
    items: [
      { label: 'Employees', to: '/app/employees', icon: <BadgeOutlinedIcon />, anyOf: [P.EMPLOYEE_READ], others: true },
      {
        label: 'Onboarding',
        to: '/app/onboarding',
        icon: <HowToRegOutlinedIcon />,
        anyOf: [P.ONBOARDING_CASE_READ, P.ONBOARDING_TEMPLATE_READ],
        others: true,
        children: [
          { label: 'Cases', to: '/app/onboarding/cases', anyOf: [P.ONBOARDING_CASE_READ] },
          { label: 'Templates', to: '/app/onboarding/templates', anyOf: [P.ONBOARDING_TEMPLATE_READ] },
        ],
      },
      {
        label: 'Leave',
        to: '/app/leave',
        icon: <BeachAccessOutlinedIcon />,
        anyOf: [P.LEAVE_APPROVE, P.LEAVE_REQUEST_READ, P.LEAVE_POLICY_MANAGE],
        others: true,
        children: [
          { label: 'Requests & approvals', to: '/app/leave/requests', anyOf: [P.LEAVE_REQUEST_READ, P.LEAVE_APPROVE] },
          { label: 'Leave calendar', to: '/app/leave/calendar', anyOf: [P.LEAVE_REQUEST_READ] },
          { label: 'Leave types', to: '/app/leave/types', anyOf: [P.LEAVE_TYPE_MANAGE] },
          { label: 'Policies', to: '/app/leave/policies', anyOf: [P.LEAVE_POLICY_MANAGE] },
        ],
      },
    ],
  },
  {
    title: 'Assets',
    items: [
      {
        label: 'Assets',
        to: '/app/assets',
        icon: <DevicesOtherOutlinedIcon />,
        anyOf: [P.ASSET_READ],
        others: true,
        children: [
          { label: 'Inventory', to: '/app/assets/inventory', anyOf: [P.ASSET_READ] },
          { label: 'Assignments', to: '/app/assets/assignments', anyOf: [P.ASSET_READ] },
          { label: 'Tags & types', to: '/app/assets/tags', anyOf: [P.ASSET_CONFIG_MANAGE] },
          { label: 'Asset audit', to: '/app/assets/audit', anyOf: [P.ASSET_AUDIT_VIEW] },
        ],
      },
    ],
  },
  {
    title: 'Organization',
    items: [
      {
        label: 'Organization',
        to: '/app/organization',
        icon: <AccountTreeOutlinedIcon />,
        anyOf: [P.DEPARTMENT_MANAGE, P.TEAM_MANAGE, P.CALENDAR_MANAGE, P.TENANT_READ],
        children: [
          { label: 'Company profile', to: '/app/organization', anyOf: [P.TENANT_READ] },
          { label: 'Departments', to: '/app/organization/departments', anyOf: [P.DEPARTMENT_READ] },
          { label: 'Teams', to: '/app/organization/teams', anyOf: [P.TEAM_READ] },
          { label: 'Work calendars', to: '/app/organization/calendars', anyOf: [P.CALENDAR_READ] },
        ],
      },
      { label: 'Locations', to: '/app/organization/locations', icon: <PlaceOutlinedIcon />, anyOf: [P.LOCATION_CREATE, P.LOCATION_UPDATE, P.LOCATION_DEACTIVATE] },
      {
        label: 'Users & roles',
        to: '/app/users',
        icon: <GroupsOutlinedIcon />,
        anyOf: [P.USER_READ, P.ROLE_READ, P.USER_INVITE],
        children: [
          { label: 'Users', to: '/app/users', anyOf: [P.USER_READ] },
          { label: 'Invitations', to: '/app/invitations', anyOf: [P.USER_INVITE] },
          { label: 'Roles', to: '/app/roles', anyOf: [P.ROLE_READ] },
        ],
      },
      { label: 'Settings', to: '/app/settings', icon: <SettingsOutlinedIcon />, anyOf: [P.CONFIG_READ, P.CUSTOM_FIELD_MANAGE, P.DOCUMENT_CATEGORY_MANAGE] },
    ],
  },
  {
    title: 'Insights',
    items: [
      { label: 'Reports', to: '/app/reports', icon: <AssessmentOutlinedIcon />, anyOf: [P.REPORT_VIEW] },
      { label: 'Audit log', to: '/app/audit', icon: <PolicyOutlinedIcon />, anyOf: [P.AUDIT_VIEW] },
      { label: 'Correction requests', to: '/app/employees/change-requests', icon: <FactCheckOutlinedIcon />, anyOf: [P.EMPLOYEE_CHANGE_REVIEW] },
    ],
  },
];
